import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Signup.css';
import { getApiUrl } from '../config/api';

function formatPhoneDisplay(digits) {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function parsePhoneDigits(formatted) {
  return formatted.replace(/\D/g, '').slice(0, 10);
}

const TenantSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';

  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: prefillEmail,
    phone: '',
    password: '',
    confirm_password: '',
  });
  const [message, setMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validatePasswords = () => {
    if (formData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      setPasswordError('Passwords do not match');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    const submitData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      password: formData.password,
    };
    if (formData.phone) {
      submitData.phone = `+1${formData.phone}`;
    }

    try {
      const response = await fetch(getApiUrl('/api/v1/auth/tenant-signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        setMessage(responseData.error || 'Signup failed. Please try again.');
        return;
      }

      localStorage.setItem('access_token', responseData.access_token);
      localStorage.setItem('refresh_token', responseData.refresh_token);
      localStorage.setItem('user_id', responseData.user_id);
      localStorage.setItem('first_name', responseData.first_name);

      setSuccess(true);
      setMessage('');
      setTimeout(() => {
        navigate('/tenant/dashboard');
      }, 3000);
    } catch (error) {
      console.error('Error:', error);
      setMessage('An error occurred. Please try again.');
    }
  };

  return (
    <div className="page-container">
      <div className="signup-wrapper">
        <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
        <div className="signup-box">
          {!success ? (
            <form onSubmit={handleSubmit}>
              <h2 className="text-xl font-semibold text-gray-800">Create your tenant account</h2>
              <div className="signup-name-row">
                <input
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <input
                type="email"
                name="email"
                placeholder="email@domain.com"
                className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="text"
                  value="+1"
                  readOnly
                  className="w-[50px] p-2 border rounded bg-gray-100 text-gray-500 text-center cursor-not-allowed"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="(555) 000-0000"
                  className="flex-1 p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={formatPhoneDisplay(formData.phone)}
                  onChange={(e) => setFormData({ ...formData, phone: parsePhoneDigits(e.target.value) })}
                />
              </div>
              <input
                type="password"
                name="password"
                placeholder="Password (min 8 characters)"
                className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <input
                type="password"
                name="confirm_password"
                placeholder="Confirm Password"
                className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                value={formData.confirm_password}
                onChange={handleChange}
                required
              />
              {passwordError && <p className="error-message">{passwordError}</p>}
              <button
                type="submit"
                className="mt-4 w-full p-2 bg-green-700 text-white rounded hover:bg-green-800"
              >
                Sign Up
              </button>
              <p className="mt-4 text-center text-gray-600">
                Already have an account?{' '}
                <span
                  className="text-green-700 cursor-pointer hover:underline"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </span>
              </p>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">&#10003;</div>
              <h2 className="text-xl font-semibold text-green-700">Account Created!</h2>
              <p className="text-gray-600 mt-2">Redirecting to your dashboard...</p>
            </div>
          )}
        </div>
        {message && !success && (
          <p className={message.includes('successfully') ? 'success-message' : 'error-message'}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default TenantSignup;
